import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { ArrowRight, Clock, Calendar, BookOpen } from 'lucide-react';
import { useMetaTags } from '@/hooks/useMetaTags';
import blogArticles from '@/data/blogArticles';

const SITE_URL = process.env.REACT_APP_BACKEND_URL || '';

const BlogIndexPage = () => {
  const featured = blogArticles[0];
  const rest = blogArticles.slice(1);

  useMetaTags({
    title: 'Blog - sfretire.com | Expertise senior et silver economie',
    'og:title': 'Blog sfretire.com - Le savoir ne prend jamais sa retraite',
    'og:description': 'Analyses, guides pratiques et tendances sur la silver economie du conseil et le talent senior.',
    'og:type': 'website',
    'og:url': `${SITE_URL}/blog`,
    'og:image': featured.image,
    'twitter:card': 'summary_large_image',
    'twitter:title': 'Blog sfretire.com',
    'twitter:description': 'Analyses, guides pratiques et tendances sur la silver economie du conseil et le talent senior.',
    'twitter:image': featured.image,
    canonical: `${SITE_URL}/blog`,
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24">
        {/* Hero */}
        <section
          data-testid="blog-hero"
          className="relative overflow-hidden py-20 lg:py-28"
          style={{
            background: 'linear-gradient(160deg, #1B2A4A 0%, #0a1020 100%)',
          }}
        >
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\' fill-rule=\'evenodd\'%3E%3Cpath d=\'M0 40L40 0H20L0 20M40 40V20L20 40\'/%3E%3C/g%3E%3C/svg%3E")' }} />
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/5 mb-6">
                <BookOpen className="w-4 h-4 text-[#E8B84B]" />
                <span className="text-sm text-white/80 tracking-wide uppercase">Blog & Insights</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white leading-tight mb-4">
                Le savoir ne prend{' '}
                <span className="italic text-[#E8B84B]">jamais</span> sa retraite
              </h1>
              <p className="text-lg text-white/60 max-w-xl">
                Analyses, guides pratiques et tendances sur la silver economie du conseil et le talent senior.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Featured Article */}
        <section className="container mx-auto px-4 lg:px-8 -mt-8 relative z-20 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="featured-article-card"
              className="group block bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-auto min-h-[320px] overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase bg-[#C8952A] text-white">
                      {featured.category}
                    </span>
                  </div>
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {featured.date}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {featured.readTime} de lecture
                    </span>
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-serif font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {featured.excerpt}
                  </p>
                  <div className="flex items-center gap-2 text-primary font-semibold text-sm group-hover:gap-3 transition-all">
                    Lire l&apos;article
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        </section>

        {/* Article Grid */}
        <section className="container mx-auto px-4 lg:px-8 pb-24" data-testid="blog-article-grid">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl font-serif font-bold text-foreground">Tous les articles</h2>
            <div className="h-px flex-1 ml-6 bg-border" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((article, index) => (
              <motion.div
                key={article.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
              >
                <Link
                  to={`/blog/${article.slug}`}
                  data-testid={`article-card-${article.slug}`}
                  className="group block bg-card rounded-xl overflow-hidden shadow-sm hover:shadow-md border border-border/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase bg-white/90 text-foreground backdrop-blur-sm">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {article.readTime}
                      </span>
                    </div>
                    <h3 className="text-lg font-serif font-bold text-foreground mb-2 group-hover:text-primary transition-colors leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4">
                      {article.excerpt}
                    </p>
                    <span className="text-primary text-sm font-semibold flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                      Lire
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section
          className="py-20"
          style={{
            background: 'linear-gradient(160deg, #1B2A4A 0%, #0a1020 100%)',
          }}
        >
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-white mb-4">
              Pret a valoriser votre expertise ?
            </h2>
            <p className="text-white/60 mb-8 max-w-lg mx-auto">
              Rejoignez sfretire.com et connectez-vous avec des entreprises qui ont besoin de votre experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/retraites"
                data-testid="blog-cta-retraite"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-[#C8952A] text-white font-semibold hover:bg-[#b8973f] transition-colors"
              >
                Je suis expert retraite
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/entreprises"
                data-testid="blog-cta-entreprise"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg border-2 border-white/30 text-white font-semibold hover:bg-white/10 transition-colors"
              >
                Je cherche un expert
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogIndexPage;
